import React, { useState } from 'react'
import { MagnifyingGlassIcon, ArrowPathIcon, ShoppingBagIcon } from '@heroicons/react/24/outline'
import mercadolibreService from '../services/mercadolibreService'
import { MercadoLibreProduct } from '../components/integracion-mercadolibre/types/mercadolibre'
import LoadingSpinner from '../components/LoadingSpinner'

interface MarketplaceProductsState {
  products: MercadoLibreProduct[]
  isLoading: boolean
  error: string | null
  total: number
  currentPage: number
  limit: number
  searchSku: string
  statusFilter: 'active' | 'paused' | 'closed' | 'all'
}

const MarketplaceProducts: React.FC = () => {
  const [state, setState] = useState<MarketplaceProductsState>({
    products: [],
    isLoading: false,
    error: null,
    total: 0,
    currentPage: 1,
    limit: 20,
    searchSku: '',
    statusFilter: 'all'
  })

  const loadProducts = async (page: number = 1, status?: string) => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }))
      
      const offset = (page - 1) * state.limit
      const params = {
        limit: state.limit,
        offset,
        ...(status && status !== 'all' && { status: status as 'active' | 'paused' | 'closed' })
      }

      const response = await mercadolibreService.getSellerProducts(params)
      
      setState(prev => ({
        ...prev,
        products: response.data.results,
        total: response.data.paging.total,
        currentPage: page,
        isLoading: false
      }))
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        error: error.response?.data?.message || 'Error al cargar los productos',
        isLoading: false
      }))
    }
  }

  const searchBySku = async () => {
    if (!state.searchSku) {
      setState(prev => ({ ...prev, error: 'Debe ingresar un SKU' }))
      return
    }

    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }))
      
      const response = await mercadolibreService.getProductBySku(state.searchSku)
      
      setState(prev => ({
        ...prev,
        products: response.data.results,
        total: response.data.results.length,
        currentPage: 1,
        isLoading: false
      }))
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        error: error.response?.data?.message || 'Error al buscar el producto',
        isLoading: false
      }))
    }
  }

  const handleStatusFilter = (status: 'active' | 'paused' | 'closed' | 'all') => {
    setState(prev => ({ ...prev, statusFilter: status }))
    loadProducts(1, status)
  }

  const handlePageChange = (page: number) => {
    loadProducts(page, state.statusFilter !== 'all' ? state.statusFilter : undefined)
  }

  const getStatusBadge = (status: string) => {
    const statusClasses = {
      active: 'bg-green-100 text-green-800',
      paused: 'bg-yellow-100 text-yellow-800',
      closed: 'bg-red-100 text-red-800'
    }
    
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusClasses[status as keyof typeof statusClasses] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    )
  }

  const totalPages = Math.ceil(state.total / state.limit)

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="  flex items-center">
          <img 
            src="https://http2.mlstatic.com/frontend-assets/ml-web-navigation/ui-navigation/5.21.22/mercadolibre/favicon.svg" 
            alt="MercadoLibre" 
            className="h-8 w-8 mr-3" 
          />
          Productos MercadoLibre
        </h1>
        <p className="text-gray-600 mt-2">
          Gestiona productos de MercadoLibre
        </p>
      </div>

      {/* Filtros y búsqueda */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Buscar por SKU
            </label>
            <div className="flex">
              <input
                type="text"
                value={state.searchSku}
                onChange={(e) => setState(prev => ({ ...prev, searchSku: e.target.value }))}
                placeholder="Ingresa el SKU del producto"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                onClick={searchBySku}
                disabled={state.isLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-r-md hover:bg-blue-700 disabled:opacity-50"
              >
                <MagnifyingGlassIcon className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Estado del producto
            </label>
            <select
              value={state.statusFilter}
              onChange={(e) => handleStatusFilter(e.target.value as any)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Todos</option>
              <option value="active">Activos</option>
              <option value="paused">Pausados</option>
              <option value="closed">Cerrados</option>
            </select>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => loadProducts(1, state.statusFilter !== 'all' ? state.statusFilter : undefined)}
            disabled={state.isLoading}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
          >
            <ArrowPathIcon className="h-5 w-5 mr-2" />
            Cargar Productos
          </button>
        </div>
      </div>

      {/* Error */}
      {state.error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <p className="text-red-800">{state.error}</p>
        </div>
      )}

      {/* Loading */}
      {state.isLoading && (
        <div className="flex justify-center py-8">
          <LoadingSpinner />
        </div>
      )}

      {/* Lista de productos */}
      {!state.isLoading && state.products.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              Productos encontrados ({state.total})
            </h3>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Producto
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    SKU
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Precio
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stock
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {state.products.map((product) => (
                  <tr key={product.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {product.thumbnail && (
                          <img
                            src={product.thumbnail}
                            alt={product.title}
                            className="h-10 w-10 rounded-lg object-cover mr-3"
                          />
                        )}
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {product.title}
                          </div>
                          <div className="text-sm text-gray-500">
                            ID: {product.id}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {product.seller_sku || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${product.price?.toLocaleString() || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {product.available_quantity || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(product.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <a
                        href={product.permalink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Ver en ML
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Paginación */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Mostrando {((state.currentPage - 1) * state.limit) + 1} a {Math.min(state.currentPage * state.limit, state.total)} de {state.total} productos
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handlePageChange(state.currentPage - 1)}
                    disabled={state.currentPage === 1}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const page = i + 1
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-3 py-1 text-sm border rounded-md ${
                          state.currentPage === page
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    )
                  })}
                  
                  <button
                    onClick={() => handlePageChange(state.currentPage + 1)}
                    disabled={state.currentPage === totalPages}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Sin productos */}
      {!state.isLoading && state.products.length === 0 && !state.error && (
        <div className="bg-white rounded-lg shadow-sm border p-8 text-center">
          <ShoppingBagIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No hay productos
          </h3>
          <p className="text-gray-500">
            Haz clic en "Cargar Productos" para ver tus productos de MercadoLibre o busca un SKU específico.
          </p>
        </div>
      )}
    </div>
  )
}

export default MarketplaceProducts
